import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSteps } from '../../../redux/walkthroughPreviewSlice';
import StepPreviewContent from './StepPreviewContent';
import { getActiveStep, setActiveStep } from '../../../redux/toolsSlice';
import { getMaterial } from '../../../redux/materialSlice';
import { HUB_ENDPOINTS } from '../utils/endpoints';
import { http_get, http_post } from '../utils/hub_client';
import { setResponseActiveStep } from '../../../redux/hubSlice';
import { getValue } from '../utils/tools';

// BL-028 — "How's this step going?" self-report control (Stuck Signals mockup).
// [value, label, dot-class]. Only stuck / skipped are learner-settable; the
// others reflect the viewer-managed state.
const SEGMENTS = [
  ['not_started', 'Not started', 'ns'],
  ['in_progress', 'In progress', 'ip'],
  ['stuck', 'Stuck', 'st'],
  ['completed', 'Completed', 'cp'],
  ['skipped', 'Skipped', 'sk'],
];

// Maps the author's default-status label (Step inspector) to a status key.
const DEFAULT_STATUS_KEY = {
  'Not started': 'not_started',
  'In progress': 'in_progress',
  Stuck: 'stuck',
  Completed: 'completed',
  Skipped: 'skipped',
};

const getRequestedStepNumber = () => {
  const params = new URLSearchParams(window.location.search || '');
  const requestedStep = Number(params.get('step'));
  return Number.isFinite(requestedStep) && requestedStep > 0 ? requestedStep : null;
};

const getRequestedStepIndex = (steps) => {
  const requestedStepNumber = getRequestedStepNumber();
  if (!requestedStepNumber) return null;

  const requestedStepIndex = requestedStepNumber - 1;
  return requestedStepIndex >= 0 && requestedStepIndex < steps.length ? requestedStepIndex : null;
};

const syncStepQueryParam = (stepNumber, { push = false } = {}) => {
  if (!Number.isFinite(stepNumber) || stepNumber <= 0) return;

  const url = new URL(window.location.href);
  if (url.searchParams.get('step') === `${stepNumber}`) return;

  url.searchParams.set('step', `${stepNumber}`);
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  const historyMethod = push ? 'pushState' : 'replaceState';
  window.history[historyMethod]({}, '', nextUrl);
};

const StepViewer = () => {
  const dispatch = useDispatch();
  const steps = useSelector((state) => getSteps(state));
  const activeStep = useSelector((state) => getActiveStep(state));
  const material = useSelector((state) => getMaterial(state));
  const completeStatus = useSelector((state) => state.hub.responseData.completeStatus);
  const { config } = material;

  const defaultColors = {
    primary: '#3BA4B4',
    background: '#F6F8F9',
    text: '#363841',
    btnColor: '#3BA4B4',
    btnTextColor: '#FFFFFF',
    config
  };

  const caHeaderColor = getValue(defaultColors, 'ca', 'caHeaderColor');
  const caHeaderFontSize = getValue(defaultColors, 'ca', 'caHeaderFontSize');
  const caTextColor = getValue(defaultColors, 'ca', 'caTextColor');

  const contentAreaStyle = {
    '--path-content-header-bg': caHeaderColor || '#F3F3F3',
    '--path-content-header-font-size': caHeaderFontSize || '18px',
    '--path-content-header-text': caTextColor || '#363841'
  };

  const [currentIndex, setCurrentIndex] = useState(0);
  const [responseHydrated, setResponseHydrated] = useState(false);
  const [statusBusy, setStatusBusy] = useState({});
  const [declaredStatus, setDeclaredStatus] = useState({});
  const [reasonDraft, setReasonDraft] = useState({});
  const pendingCurrentStepRef = useRef(null);
  const pendingScrollStepIndexRef = useRef(null);
  const hasSkippedInitialPersistRef = useRef(false);
  const stepRefs = useRef({});

  const scrollToStepIndex = (stepIndex, behavior = 'smooth') => {
    const stepNode = stepRefs.current?.[stepIndex];
    if (!stepNode) return;

    stepNode.scrollIntoView({
      behavior,
      block: 'start'
    });
  };

  useEffect(() => {
    const hubUrl = window.location.pathname.split('/')[2];
    pendingCurrentStepRef.current = null;
    hasSkippedInitialPersistRef.current = false;

    if (hubUrl === 'hub' && material.id) {
      setResponseHydrated(false);
      fetchResponse();
      return;
    }

    setResponseHydrated(true);
  }, [material.id]);

  useEffect(() => {
    if (!Array.isArray(steps) || steps.length === 0) return;
    const hubUrl = window.location.pathname.split('/')[2];
    const isHubMode = hubUrl === 'hub' && material?.id;

    if (isHubMode && !responseHydrated) return;

    const requestedStepIndex = isHubMode ? getRequestedStepIndex(steps) : null;
    if (requestedStepIndex !== null) {
      dispatch(setActiveStep(steps[requestedStepIndex]));
      setCurrentIndex((prevIndex) => (prevIndex === requestedStepIndex ? prevIndex : requestedStepIndex));
      pendingScrollStepIndexRef.current = requestedStepIndex;
      syncStepQueryParam(requestedStepIndex + 1);
      return;
    }

    const activeStepId = activeStep?.id;
    const preservedIndex = activeStepId ? steps.findIndex((step) => step.id === activeStepId) : -1;

    if (preservedIndex >= 0) {
      dispatch(setActiveStep(steps[preservedIndex]));
      setCurrentIndex((prevIndex) => (prevIndex === preservedIndex ? prevIndex : preservedIndex));
      if (isHubMode) {
        syncStepQueryParam(preservedIndex + 1);
      }
      return;
    }

    let fallbackIndex = 0;
    if (isHubMode && pendingCurrentStepRef.current) {
      const requestedIndex = Number(pendingCurrentStepRef.current) - 1;
      if (requestedIndex >= 0 && requestedIndex < steps.length) {
        fallbackIndex = requestedIndex;
      }
    }

    dispatch(setActiveStep(steps[fallbackIndex]));
    setCurrentIndex((prevIndex) => (prevIndex === fallbackIndex ? prevIndex : fallbackIndex));
    if (isHubMode) {
      pendingScrollStepIndexRef.current = fallbackIndex;
      syncStepQueryParam(fallbackIndex + 1);
    }
  }, [steps, activeStep?.id, dispatch, material?.id, responseHydrated]);

  useEffect(() => {
    const hubUrl = window.location.pathname.split('/')[2];
    if (hubUrl !== 'hub' || !Array.isArray(steps) || steps.length === 0) return undefined;

    const handlePopState = () => {
      const requestedStepIndex = getRequestedStepIndex(steps);
      if (requestedStepIndex === null) return;

      dispatch(setActiveStep(steps[requestedStepIndex]));
      setCurrentIndex(requestedStepIndex);
      pendingScrollStepIndexRef.current = requestedStepIndex;
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch, steps]);

  const fetchResponse = async () => {
    const url = HUB_ENDPOINTS.GET_RESPONSE(material.id);
    try {
      const res = await http_get(url);
      const metricData = res.data?.data || {};
      const responseRecords = Array.isArray(metricData.response) ? metricData.response : [];
      const currentStep = Number(metricData.current_step);

      pendingCurrentStepRef.current = Number.isFinite(currentStep) && currentStep > 0 ? currentStep : null;

      const responseData = {
        minutes: metricData.minutes || 0,
        response: responseRecords
      };
      const nextCompleteStatus = responseRecords.reduce((acc, current) => {
        acc[current.id] = current.completion;
        return acc;
      }, {});
      dispatch(setResponseActiveStep({ ...responseData, completeStatus: nextCompleteStatus }));
      return responseData;
    } catch (error) {
      console.error('Error :', error);
    } finally {
      setResponseHydrated(true);
    }
  };

  const handleStepChange = (step, index, { push = true } = {}) => {
    if (window.location.pathname.split('/')[2] === 'hub') {
      pendingScrollStepIndexRef.current = index;
      syncStepQueryParam(index + 1, { push });
    }
    dispatch(setActiveStep(step));
    setCurrentIndex(index);
  };

  const handleNextStep = async () => {
    const hubUrl = window.location.pathname.split('/')[2];
    if (hubUrl === 'hub') {
      await saveResponse();
    }

    if (currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];
      handleStepChange(nextStep, currentIndex + 1, { push: false });
    }
  };

  // Jump to a step by its 1-based number (used by "Go to step" buttons).
  const goToStep = (value) => {
    const idx = (parseInt(value, 10) || 0) - 1;
    const target = steps[idx];
    if (target) handleStepChange(target, idx, { push: false });
  };

  const saveResponse = async () => {
    const url = HUB_ENDPOINTS.SAVE_RESPONSE(material.id);
    const previousData = await fetchResponse();

    let newData;
    if (previousData && Array.isArray(previousData.response)) {
      newData = [...previousData.response];
      const existingIndex = newData.findIndex((entry) => entry.id === activeStep.id);
      if (existingIndex !== -1) {
        newData = newData.map((item) =>
          item.id === activeStep.id ? { ...item, completion: true } : item
        );
      } else {
        newData.push({ id: activeStep.id, completion: true });
      }
    } else {
      newData = [{ id: activeStep.id, completion: true }];
    }

    const newResponse = {
      minutes: previousData ? previousData.minutes : 0,
      response: newData
    };

    try {
      const res = await http_post(url, newResponse);
      const materials = res.data.data;
      dispatch(setResponseActiveStep({ ...materials }));
      const updatedCompleteStatus = { ...completeStatus, [activeStep.id]: true };
      dispatch(setResponseActiveStep({ ...materials, completeStatus: updatedCompleteStatus }));
    } catch (error) {
      console.error('Error completing step:', error);
    }
  };

  const saveCurrentStep = async (currentStepNumber) => {
    const url = HUB_ENDPOINTS.SAVE_CURRENT_STEP(material.id);
    try {
      await http_post(url, { current_step: currentStepNumber });
    } catch (error) {
      console.error('Error saving current step:', error);
    }
  };

  useEffect(() => {
    const hubUrl = window.location.pathname.split('/')[2];
    if (hubUrl !== 'hub' || !material?.id || !activeStep?.id || !responseHydrated) return;

    const foundIndex = steps.findIndex((item) => item.id === activeStep.id);
    if (foundIndex < 0) return;

    if (!hasSkippedInitialPersistRef.current) {
      hasSkippedInitialPersistRef.current = true;
      if (pendingCurrentStepRef.current) return;
    }

    saveCurrentStep(foundIndex + 1);
  }, [activeStep?.id, material?.id, responseHydrated, steps]);

  useEffect(() => {
    const hubUrl = window.location.pathname.split('/')[2];
    if (hubUrl !== 'hub') return;

    const requestedIndex = pendingScrollStepIndexRef.current;
    if (requestedIndex === null || requestedIndex === undefined) return;

    requestAnimationFrame(() => {
      scrollToStepIndex(requestedIndex);
      pendingScrollStepIndexRef.current = null;
    });
  }, [activeStep?.id, steps]);

  if (!Array.isArray(steps) || steps.length === 0) return null;

  // BL-028 — learner self-reports a step's status. Only Stuck / Skipped are
  // learner-settable; the others are managed by the viewer flow.
  const postStatus = async (step, index, status, reason) => {
    setStatusBusy((b) => ({ ...b, [step.id]: true }));
    try {
      await http_post(HUB_ENDPOINTS.SET_STEP_STATUS(material.id), {
        step_uid: step.id,
        status,
        reason: reason || '',
        step_position: index + 1,
      });
    } catch (error) {
      console.error('Error setting step status:', error);
    } finally {
      setStatusBusy((b) => ({ ...b, [step.id]: false }));
    }
  };

  const selectStatus = (step, index, status) => {
    if (status !== 'stuck' && status !== 'skipped') return; // system-managed states
    setDeclaredStatus((d) => ({ ...d, [step.id]: status }));
    postStatus(step, index, status, status === 'stuck' ? reasonDraft[step.id] : '');
  };

  return (
    <div className="step-list path-step-viewer" style={contentAreaStyle}>
      {steps.map((step, index) => {
        const isActive = activeStep?.id === step.id;
        const isComplete = completeStatus && completeStatus[step.id] ? completeStatus[step.id] : false;
        const stateClass = isActive ? 'is-active' : isComplete ? 'is-complete' : 'is-pending';
        const statusLabel = isActive ? 'Currently active' : isComplete ? 'Completed' : 'Pending';
        const title = step.title || step.name || `Step ${index + 1}`;
        const fallbackStatus = DEFAULT_STATUS_KEY[step.default_status] || 'not_started';
        const currentStatus = declaredStatus[step.id]
          || (isComplete ? 'completed' : isActive ? 'in_progress' : fallbackStatus);

        return (
          <article
            key={step.id}
            ref={(node) => {
              stepRefs.current[index] = node;
            }}
            className={`accordion-cover step path-step-card ${stateClass}${currentStatus === 'stuck' ? ' is-stuck' : ''}`}
            data-step-state={isActive ? 'active' : isComplete ? 'complete' : 'pending'}
          >
            <button
              type="button"
              onClick={() => handleStepChange(step, index)}
              className="header-accordion header-switch-accordion path-step-card__header"
            >
              <div className="path-step-card__summary">
                <span className="path-step-card__number">{index + 1}</span>
                <div className="path-step-card__copy">
                  <span className="path-step-card__status">{statusLabel}</span>
                  <span className="text-switch-accordion path-step-card__title">{title}</span>
                </div>
              </div>

              <div className="path-step-card__trailing" aria-hidden="true">
                {isComplete ? (
                  <span className="path-step-card__complete-indicator">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="10" fill="currentColor" />
                      <path d="M8.529 12.974 5.95 10.395a.75.75 0 0 1 1.06-1.06l1.519 1.519 4.458-4.458a.75.75 0 1 1 1.06 1.06l-5.518 5.518Z" fill="#fff" />
                    </svg>
                  </span>
                ) : isActive ? (
                  <span className="path-step-card__active-indicator">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <circle cx="11" cy="11" r="10" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                  </span>
                ) : (
                  <span className="path-step-card__pending-indicator">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                      <path d="M8.25 5.5 13.75 11l-5.5 5.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                )}
              </div>
            </button>

            <div className={`body-accordion path-step-card__body ${isActive ? 'active body-switch-accordion' : ''}`}>
              {isActive && (
                <>
                  <StepPreviewContent
                    moveToNextStep={handleNextStep}
                    isComplete={isComplete}
                    stepIndex={index}
                    totalSteps={steps.length}
                    goToStep={goToStep}
                  />
                  {step?.show_status_control !== false && (
                  <div className="path-step-card__statusblock">
                    <div className="path-step-card__sgk">How&rsquo;s this step going?</div>
                    <div className="path-step-card__seg">
                      {SEGMENTS.map(([value, label, dot]) => {
                        const on = currentStatus === value;
                        const actionable = value === 'stuck' || value === 'skipped';
                        const cls = [
                          'path-step-card__seg-s',
                          on ? 'on' : '',
                          on && value === 'stuck' ? 'on-st' : '',
                          on && value === 'completed' ? 'on-cp' : '',
                          actionable ? '' : 'is-static',
                        ].filter(Boolean).join(' ');
                        return (
                          <button
                            key={value}
                            type="button"
                            className={cls}
                            disabled={statusBusy[step.id]}
                            onClick={actionable ? () => selectStatus(step, index, value) : undefined}
                          >
                            <span className={`path-step-card__seg-d d-${dot}`} />{label}
                          </button>
                        );
                      })}
                    </div>
                    {currentStatus === 'stuck' && (
                      <div className="path-step-card__stuckbox">
                        <div className="path-step-card__stuckbox-t">&#9873; What&rsquo;s blocking you? (optional)</div>
                        <textarea
                          className="path-step-card__stuckbox-ta"
                          value={reasonDraft[step.id] || ''}
                          onChange={(e) => setReasonDraft((r) => ({ ...r, [step.id]: e.target.value }))}
                          onBlur={() => postStatus(step, index, 'stuck', reasonDraft[step.id])}
                          placeholder="e.g. the video won't load on my browser"
                        />
                        <div className="path-step-card__stuckbox-note">&#10003; Your team has been notified and can jump in to help.</div>
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default StepViewer;
